import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Stack,
  TablePagination,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/system';

import SbButton from '@Components/Button';
import DateRangePicker from '@Components/DateRangePicker';
import FilterSelect from '@Components/Filter/Dropdown';
import FilterSearch from '@Components/Filter/Search';

import { getCurrencySymbolByName } from '@Constants/countriesWithCurrency';
import { ShipmentObj } from '@Reducers/Invoice/Type';

import { formatNumber, useLocale } from '../../../../utils/formatNumber';
import { AccountHolderUnbilledData } from '../../Type';
import {
  MappedShipmentRow,
  SearchFilterList,
  SelectedParamType,
  ShipmentTypeList,
  getAccountGroupKey,
  isSameAccountEntry,
  mapShipmentsToGridRows,
} from '../utils/invoiceAccountUtils';

const EMPTY_SHIPMENTS: ShipmentObj[] = [];

interface TextWithTooltipProps {
  text: string | number | null | undefined;
  title?: string;
  sx?: object;
  className?: string;
  component?: React.ElementType;
}

const TextWithTooltip = React.memo(function TextWithTooltip({
  text,
  title,
  sx = {},
  className = '',
  component = 'span',
}: TextWithTooltipProps) {
  const displayString = text !== null && text !== undefined && text !== '' ? String(text) : '-';
  const tooltipContent = title || (text ? String(text) : '');

  if (!tooltipContent || tooltipContent === '-') {
    return (
      <Typography
        component={component}
        className={className}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
          ...sx,
        }}
      >
        {displayString}
      </Typography>
    );
  }

  return (
    <Tooltip title={tooltipContent} arrow enterDelay={250} placement="top">
      <Typography
        component={component}
        className={className}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
          cursor: 'inherit',
          ...sx,
        }}
      >
        {displayString}
      </Typography>
    </Tooltip>
  );
});

interface ShipmentDetailRowProps {
  accountData: AccountHolderUnbilledData;
  rawShipment: ShipmentObj;
  mappedRow: MappedShipmentRow;
  currencySymbol: string;
  locale: string;
  isSelected: boolean;
  isExpanded: boolean;
  onToggleSelect: (accountData: AccountHolderUnbilledData, shipment: ShipmentObj, checked: boolean) => void;
  onToggleExpand: (shipmentId: string) => void;
}

const ShipmentDetailRow = React.memo(function ShipmentDetailRow(props: ShipmentDetailRowProps) {
  const {
    accountData,
    rawShipment,
    mappedRow,
    currencySymbol,
    locale,
    isSelected,
    isExpanded,
    onToggleSelect,
    onToggleExpand,
  } = props;

  const theme = useTheme();
  const shipmentId = rawShipment._id || String(mappedRow.id);

  const handleCheckboxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      onToggleSelect(accountData, rawShipment, e.target.checked);
    },
    [accountData, rawShipment, onToggleSelect],
  );

  const handleCheckboxClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleLinkClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleExpandToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggleExpand(shipmentId);
    },
    [onToggleExpand, shipmentId],
  );

  const pricingBreakdown = rawShipment?.sellingPricing?.pricing || [];
  const taxPrice = rawShipment?.sellingPricing?.totalTaxPrice || 0;

  const fullRouteSummary = `${mappedRow.shipper?.name || '-'} (${mappedRow.shipper?.city || '-'}, ${
    mappedRow.shipper?.country || '-'
  }) → ${mappedRow.consignee?.name || '-'} (${mappedRow.consignee?.city || '-'}, ${
    mappedRow.consignee?.country || '-'
  })`;

  return (
    <Box
      sx={{
        borderRadius: '10px',
        border: `1px solid ${isSelected ? alpha(theme.palette.primary.main, 0.45) : theme.palette.divider}`,
        bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.03) : 'background.paper',
        transition: 'border-color 0.18s ease, background-color 0.18s ease',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.5),
        },
      }}
    >
      {/* Collapsed Shipment Summary Row */}
      <Box
        onClick={handleExpandToggle}
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '36px 1fr auto',
            md: '36px minmax(130px, 1.1fr) minmax(170px, 1.5fr) minmax(120px, 1fr) minmax(100px, 0.9fr) minmax(115px, 1fr) 36px',
          },
          alignItems: 'center',
          columnGap: 1.5,
          rowGap: 1,
          px: 1.5,
          py: 1,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        {/* Independent Selection Checkbox */}
        <Box onClick={handleCheckboxClick} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Checkbox
            size="small"
            checked={isSelected}
            onChange={handleCheckboxChange}
            sx={{ p: 0.5 }}
            inputProps={{ 'aria-label': `Select shipment ${mappedRow.shipmentNo}` }}
          />
        </Box>

        {/* Shipment Number & Date */}
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title={`Open shipment ${mappedRow.shipmentNo}`} arrow placement="top">
              <Link
                to={`/shipment/admin/edit/${mappedRow.shipmentNo}`}
                onClick={handleLinkClick}
                className="underline"
                style={{
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  fontSize: '0.84rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                  maxWidth: '100%',
                }}
              >
                {mappedRow.shipmentNo}
              </Link>
            </Tooltip>
          </Stack>
          <TextWithTooltip text={mappedRow.date} sx={{ fontSize: '0.74rem', color: 'text.secondary', mt: 0.15 }} />
        </Box>

        {/* Route & Customer Summary */}
        <Box sx={{ minWidth: 0, display: { xs: 'none', md: 'block' } }}>
          <TextWithTooltip
            text={`${mappedRow.shipper?.name || '-'} → ${mappedRow.consignee?.name || '-'}`}
            title={fullRouteSummary}
            sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary' }}
          />
          <TextWithTooltip
            text={`${mappedRow.shipper?.city || '-'}, ${mappedRow.shipper?.country || '-'} → ${
              mappedRow.consignee?.city || '-'
            }, ${mappedRow.consignee?.country || '-'}`}
            title={fullRouteSummary}
            sx={{ fontSize: '0.73rem', color: 'text.secondary' }}
          />
        </Box>

        {/* Service & Status */}
        <Box sx={{ minWidth: 0, display: { xs: 'none', md: 'block' } }}>
          <Tooltip title={`Status: ${mappedRow.status}`} arrow placement="top">
            <Chip
              size="small"
              label={mappedRow.status}
              sx={{
                height: 20,
                fontSize: '0.68rem',
                fontWeight: 600,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: 'text.primary',
                mb: 0.35,
                maxWidth: '100%',
              }}
            />
          </Tooltip>
          <TextWithTooltip text={mappedRow.serviceName} sx={{ fontSize: '0.73rem', color: 'text.secondary' }} />
        </Box>

        {/* Weight & Packages */}
        <Box sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'right', minWidth: 0 }}>
          <TextWithTooltip
            text={mappedRow.weight}
            sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary' }}
          />
          <TextWithTooltip
            text={`${mappedRow.packages} ${mappedRow.packages === 1 ? 'pkg' : 'pkgs'}`}
            sx={{ fontSize: '0.73rem', color: 'text.secondary' }}
          />
        </Box>

        {/* Amount */}
        <Box sx={{ textAlign: 'right', minWidth: 0 }}>
          <TextWithTooltip
            text={`${currencySymbol} ${mappedRow.totalAmount}`}
            title={`Amount: ${currencySymbol} ${mappedRow.totalAmount} (${mappedRow.currency})`}
            sx={{ fontSize: '0.86rem', fontWeight: 700, color: 'text.primary' }}
          />
          <TextWithTooltip
            text={accountData.accountNumber}
            title={`Account: ${accountData.accountNumber}`}
            sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
          />
        </Box>

        {/* Explicit Expand / Collapse Control */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Tooltip title={isExpanded ? 'Hide shipment details' : 'View shipment details'} arrow>
            <IconButton
              size="small"
              onClick={handleExpandToggle}
              aria-label={isExpanded ? 'Collapse shipment details' : 'Expand shipment details'}
              sx={{
                p: 0.4,
                color: isExpanded ? 'primary.main' : 'text.secondary',
                bgcolor: isExpanded ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease, background-color 0.2s ease',
              }}
            >
              <KeyboardArrowDownIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Collapsible Detailed Shipment Section (Collapsed by Default) */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Divider />
        <Box
          sx={{
            p: 1.75,
            bgcolor: alpha(theme.palette.text.primary, 0.018),
          }}
        >
          <Grid container spacing={2}>
            {/* Shipper Details */}
            <Grid item xs={12} sm={4}>
              <Stack direction="row" spacing={0.75} alignItems="center" mb={0.5}>
                <PlaceOutlinedIcon sx={{ fontSize: 15, color: 'primary.main' }} />
                <Typography
                  sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}
                >
                  Shipper Details
                </Typography>
              </Stack>
              <TextWithTooltip
                text={mappedRow.shipper?.name}
                sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary' }}
              />
              {mappedRow.shipper?.companyName && (
                <TextWithTooltip
                  text={mappedRow.shipper.companyName}
                  sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
                />
              )}
              <TextWithTooltip
                text={[mappedRow.shipper?.line1, mappedRow.shipper?.line2].filter(Boolean).join(', ')}
                sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}
              />
              <TextWithTooltip
                text={`${mappedRow.shipper?.city || '-'} - ${mappedRow.shipper?.pincode || '-'}, ${
                  mappedRow.shipper?.country || '-'
                }`}
                sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
              />
              {mappedRow.shipper?.email && (
                <TextWithTooltip
                  text={mappedRow.shipper.email}
                  sx={{ fontSize: '0.73rem', color: 'text.secondary', mt: 0.25 }}
                />
              )}
            </Grid>

            {/* Consignee Details */}
            <Grid item xs={12} sm={4}>
              <Stack direction="row" spacing={0.75} alignItems="center" mb={0.5}>
                <PlaceOutlinedIcon sx={{ fontSize: 15, color: 'secondary.main' }} />
                <Typography
                  sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}
                >
                  Consignee Details
                </Typography>
              </Stack>
              <TextWithTooltip
                text={mappedRow.consignee?.name}
                sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary' }}
              />
              {mappedRow.consignee?.companyName && (
                <TextWithTooltip
                  text={mappedRow.consignee.companyName}
                  sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
                />
              )}
              <TextWithTooltip
                text={[mappedRow.consignee?.line1, mappedRow.consignee?.line2].filter(Boolean).join(', ')}
                sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}
              />
              <TextWithTooltip
                text={`${mappedRow.consignee?.city || '-'} - ${mappedRow.consignee?.pincode || '-'}, ${
                  mappedRow.consignee?.country || '-'
                }`}
                sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
              />
              {mappedRow.consignee?.email && (
                <TextWithTooltip
                  text={mappedRow.consignee.email}
                  sx={{ fontSize: '0.73rem', color: 'text.secondary', mt: 0.25 }}
                />
              )}
            </Grid>

            {/* Service, Carrier & Pricing Breakdown */}
            <Grid item xs={12} sm={4}>
              <Stack direction="row" spacing={0.75} alignItems="center" mb={0.5}>
                <ReceiptOutlinedIcon sx={{ fontSize: 15, color: 'primary.main' }} />
                <Typography
                  sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}
                >
                  Service & Charge Breakdown
                </Typography>
              </Stack>
              <Stack spacing={0.4}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', flexShrink: 0 }}>
                    Service / AWB:
                  </Typography>
                  <TextWithTooltip
                    text={`${mappedRow.serviceName} (${mappedRow.awb})`}
                    sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary', textAlign: 'right' }}
                  />
                </Stack>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', flexShrink: 0 }}>
                    Weight / Packages:
                  </Typography>
                  <TextWithTooltip
                    text={`${mappedRow.weight} • ${mappedRow.packages} pkg(s)`}
                    sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary', textAlign: 'right' }}
                  />
                </Stack>
                {pricingBreakdown.slice(0, 4).map(charge => (
                  <Stack key={charge._id || charge.key} direction="row" justifyContent="space-between" spacing={1}>
                    <TextWithTooltip
                      text={`${charge.displayKey || charge.key}:`}
                      sx={{ fontSize: '0.74rem', color: 'text.secondary' }}
                    />
                    <TextWithTooltip
                      text={`${currencySymbol} ${formatNumber(charge.value || 0, locale)}`}
                      sx={{ fontSize: '0.74rem', fontWeight: 500, color: 'text.primary', textAlign: 'right' }}
                    />
                  </Stack>
                ))}
                {taxPrice > 0 && (
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary', flexShrink: 0 }}>
                      Total Tax:
                    </Typography>
                    <TextWithTooltip
                      text={`${currencySymbol} ${formatNumber(taxPrice, locale)}`}
                      sx={{ fontSize: '0.74rem', fontWeight: 500, color: 'text.primary', textAlign: 'right' }}
                    />
                  </Stack>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
});

interface AccountShipmentCardProps {
  data: AccountHolderUnbilledData;
  groupKey: string;
  orgName: string;
  locale: string;
  selectedShipments: ShipmentObj[];
  isExpanded: boolean;
  onToggleExpandAccount: (groupKey: string) => void;
  onSelectAccountGroup: (data: AccountHolderUnbilledData, isChecked: boolean) => void;
  onToggleSingleShipment: (data: AccountHolderUnbilledData, shipment: ShipmentObj, isChecked: boolean) => void;
}

const AccountShipmentCard = React.memo(function AccountShipmentCard(props: AccountShipmentCardProps) {
  const {
    data,
    groupKey,
    orgName,
    locale,
    selectedShipments,
    isExpanded,
    onToggleExpandAccount,
    onSelectAccountGroup,
    onToggleSingleShipment,
  } = props;

  const theme = useTheme();
  const [expandedShipments, setExpandedShipments] = useState<Record<string, boolean>>({});

  const handleToggleExpandShipment = useCallback((shipmentId: string) => {
    setExpandedShipments(prev => ({ ...prev, [shipmentId]: !prev[shipmentId] }));
  }, []);

  const shipments = data.shipments || EMPTY_SHIPMENTS;
  const totalShipments = shipments.length;
  const selectedCount = selectedShipments.length;

  const isAccountFullySelected = totalShipments > 0 && selectedCount === totalShipments;
  const isAccountPartiallySelected = selectedCount > 0 && selectedCount < totalShipments;

  const selectedIdsSet = useMemo(() => new Set(selectedShipments.map(s => s._id)), [selectedShipments]);

  const currencySymbol = useMemo(
    () =>
      getCurrencySymbolByName(shipments[0]?.sellingPricing?.currency) ||
      getCurrencySymbolByName(shipments[0]?._packageId?.currency) ||
      '',
    [shipments],
  );

  const accountSelectedTotal = useMemo(
    () => selectedShipments.reduce((sum, s) => sum + (s?.sellingPricing?.totalPricing || 0), 0),
    [selectedShipments],
  );

  const mappedShipmentRows = useMemo(
    () => mapShipmentsToGridRows(shipments, orgName, locale),
    [shipments, orgName, locale],
  );

  const primaryShipmentPreview = useMemo(() => {
    if (!shipments.length) return 'No shipments';
    const firstNo = shipments[0]?.shipmentNumber || '';
    return shipments.length > 1 ? `${firstNo} (+${shipments.length - 1} more)` : firstNo;
  }, [shipments]);

  const primaryServicePreview = shipments[0]?.sellingPricing?.serviceName || '';

  const customerName = data?.company ? data.company.name : data?.user ? data.user.name : '-';
  const customerEmail = data?.company ? data.company.email : data?.user ? data.user.email : '-';

  const handleAccountCheckboxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      onSelectAccountGroup(data, e.target.checked);
    },
    [data, onSelectAccountGroup],
  );

  const handleStopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleToggleRowExpand = useCallback(() => {
    onToggleExpandAccount(groupKey);
  }, [onToggleExpandAccount, groupKey]);

  return (
    <Box
      sx={{
        borderRadius: '12px',
        border: `1px solid ${selectedCount > 0 ? alpha(theme.palette.primary.main, 0.5) : theme.palette.divider}`,
        borderLeft:
          selectedCount > 0 ? `4px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
        bgcolor: selectedCount > 0 ? alpha(theme.palette.primary.main, 0.015) : 'background.paper',
        transition: 'all 0.18s ease',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.45),
        },
      }}
    >
      {/* Collapsed Account & Shipment Summary Header */}
      <Box
        onClick={handleToggleRowExpand}
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '38px 1fr auto',
            md: '38px minmax(170px, 1.4fr) minmax(150px, 1.3fr) minmax(110px, 1fr) minmax(130px, 1.1fr) auto',
          },
          alignItems: 'center',
          columnGap: 1.5,
          rowGap: 1,
          px: { xs: 1.25, sm: 2 },
          py: 1.2,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        {/* Checkbox - Independent from Expansion */}
        <Box onClick={handleStopPropagation} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Checkbox
            size="small"
            checked={isAccountFullySelected}
            indeterminate={isAccountPartiallySelected}
            onChange={handleAccountCheckboxChange}
            sx={{ p: 0.5 }}
            inputProps={{ 'aria-label': `Select account ${data.accountNumber}` }}
          />
        </Box>

        {/* Account No. & Customer Details */}
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="nowrap">
            <Tooltip title={`Account Number: ${data.accountNumber}`} arrow placement="top">
              <Chip
                size="small"
                label={data.accountNumber}
                sx={{
                  height: 22,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  borderRadius: '6px',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                  flexShrink: 0,
                }}
              />
            </Tooltip>
            <TextWithTooltip
              text={customerName}
              title={`Customer / Company: ${customerName}`}
              sx={{ fontWeight: 600, fontSize: '0.88rem', color: 'text.primary' }}
            />
          </Stack>
          <TextWithTooltip
            text={customerEmail}
            title={`Email: ${customerEmail}`}
            sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}
          />
        </Box>

        {/* Shipment Number Summary & Service Info */}
        <Box sx={{ minWidth: 0, display: { xs: 'none', md: 'block' } }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <LocalShippingOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary', flexShrink: 0 }} />
            <TextWithTooltip
              text={primaryShipmentPreview}
              title={`Shipments in this account: ${shipments.map(s => s.shipmentNumber).join(', ')}`}
              sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary' }}
            />
          </Stack>
          <TextWithTooltip
            text={
              primaryServicePreview
                ? `${data._systemId?.name || '-'} • ${primaryServicePreview}`
                : data._systemId?.name || '-'
            }
            title={`System: ${data._systemId?.name || '-'}, Service: ${primaryServicePreview || '-'}`}
            sx={{ fontSize: '0.73rem', color: 'text.secondary', mt: 0.2 }}
          />
        </Box>

        {/* Shipment Count & Route Type Badge */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'flex-start', gap: 0.4 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary', flexShrink: 0 }}>
              {totalShipments} {totalShipments === 1 ? 'Shipment' : 'Shipments'}
            </Typography>
            {selectedCount > 0 && (
              <Chip
                size="small"
                color="primary"
                label={`${selectedCount} selected`}
                sx={{ height: 19, fontSize: '0.68rem', fontWeight: 700 }}
              />
            )}
          </Stack>
          {orgName !== 'Cargo Solutions Network' && data.shipmentRouteType && (
            <TextWithTooltip
              text={data.shipmentRouteType}
              title={`Shipment Route Type: ${data.shipmentRouteType}`}
              sx={{
                fontSize: '0.72rem',
                fontWeight: 600,
                textTransform: 'capitalize',
                color: 'text.secondary',
              }}
            />
          )}
        </Box>

        {/* Total Amount & Selected Amount */}
        <Box sx={{ textAlign: 'right', minWidth: 0 }}>
          <TextWithTooltip
            text={`${currencySymbol} ${formatNumber(parseFloat(data.totalAmount || '0'), locale)}`}
            title={`Unbilled Total: ${currencySymbol} ${formatNumber(parseFloat(data.totalAmount || '0'), locale)}`}
            sx={{ fontSize: '0.92rem', fontWeight: 700, color: 'text.primary' }}
          />
          {selectedCount > 0 ? (
            <TextWithTooltip
              text={`Selected: ${currencySymbol} ${formatNumber(accountSelectedTotal, locale)}`}
              title={`Selected Subtotal: ${currencySymbol} ${formatNumber(accountSelectedTotal, locale)}`}
              sx={{ fontSize: '0.73rem', fontWeight: 700, color: 'primary.main' }}
            />
          ) : (
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Unbilled total</Typography>
          )}
        </Box>

        {/* Explicit Expand / Collapse Control */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <Tooltip title={isExpanded ? 'Collapse shipments list' : 'Expand shipments list'} arrow>
            <Box
              component="button"
              type="button"
              onClick={e => {
                e.stopPropagation();
                handleToggleRowExpand();
              }}
              aria-expanded={isExpanded}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '5px 10px',
                borderRadius: '8px',
                border: `1px solid ${theme.palette.divider}`,
                background: isExpanded ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                color: isExpanded ? theme.palette.primary.main : theme.palette.text.secondary,
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <span>{isExpanded ? 'Hide' : 'Shipments'}</span>
              <KeyboardArrowDownIcon
                fontSize="small"
                sx={{
                  fontSize: 18,
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {/* Expandable Shipments Section (Collapsed by Default) */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Divider />
        <Box
          sx={{
            p: { xs: 1.25, sm: 1.75 },
            bgcolor: alpha(theme.palette.primary.main, 0.02),
          }}
        >
          {/* Sub-header inside expanded account */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1}
            mb={1.25}
            px={0.5}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'text.secondary' }}>
                SHIPMENTS FOR ACCOUNT {data.accountNumber} ({selectedCount}/{totalShipments} SELECTED)
              </Typography>
              <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>
                • Click any shipment row to inspect full shipper, consignee, and charge details
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'primary.main' }}>
              Account Selected Subtotal: {currencySymbol} {formatNumber(accountSelectedTotal, locale)}
            </Typography>
          </Stack>

          {/* Individual Shipment Rows — No fixed maxHeight to allow natural scrolling */}
          <Stack spacing={1}>
            {mappedShipmentRows.map((row, sIdx) => {
              const rawShipment = shipments[sIdx];
              if (!rawShipment) return null;
              const shipmentKey = rawShipment._id || String(row.id);
              const isShipmentChecked = selectedIdsSet.has(rawShipment._id);
              const isShipmentExpanded = Boolean(expandedShipments[shipmentKey]);

              return (
                <ShipmentDetailRow
                  key={shipmentKey}
                  accountData={data}
                  rawShipment={rawShipment}
                  mappedRow={row}
                  currencySymbol={currencySymbol}
                  locale={locale}
                  isSelected={isShipmentChecked}
                  isExpanded={isShipmentExpanded}
                  onToggleSelect={onToggleSingleShipment}
                  onToggleExpand={handleToggleExpandShipment}
                />
              );
            })}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
});

export interface ShipmentDetailsSectionProps {
  list: AccountHolderUnbilledData[];
  listLoading: boolean;
  totalCount: number;
  currentPage: number;
  perPage: number;
  orgName: string;
  activeSystemList: { display: string; value: string }[];
  filterSelectValue: string;
  filterText: string;
  dateRange: { startDate: Date; endDate: Date };
  system: string[];
  shipmentType: string[];
  selectedAccountRows: SelectedParamType[];
  expandedAccounts: Record<string, boolean>;
  onSearchSelectChange: (value: string) => void;
  onSearchTextChange: (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onTextSearch: () => void;
  onTextClear: () => void;
  onDateChange: ({ startDate, endDate }: { startDate: Date; endDate: Date }, isOpen: boolean) => void;
  onSystemFilter: (selected: string[]) => void;
  onTypeFilter: (selected: string[]) => void;
  onClearFilters: () => void;
  onPageChange: (_: unknown, page: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSelectAllAccounts: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectAccountGroup: (data: AccountHolderUnbilledData, isChecked: boolean) => void;
  onToggleSingleShipment: (data: AccountHolderUnbilledData, shipment: ShipmentObj, isChecked: boolean) => void;
  onToggleExpandAccount: (groupKey: string) => void;
}

const ShipmentDetailsSection = React.memo(function ShipmentDetailsSection(props: ShipmentDetailsSectionProps) {
  const {
    list,
    listLoading,
    totalCount,
    currentPage,
    perPage,
    orgName,
    activeSystemList,
    filterSelectValue,
    filterText,
    dateRange,
    system,
    shipmentType,
    selectedAccountRows,
    expandedAccounts,
    onSearchSelectChange,
    onSearchTextChange,
    onTextSearch,
    onTextClear,
    onDateChange,
    onSystemFilter,
    onTypeFilter,
    onClearFilters,
    onPageChange,
    onRowsPerPageChange,
    onSelectAllAccounts,
    onSelectAccountGroup,
    onToggleSingleShipment,
    onToggleExpandAccount,
  } = props;

  const theme = useTheme();
  const locale = useLocale();

  const isAllAccountsSelected = useMemo(() => {
    if (!list.length) return false;
    const hasCanGenerateFlag = list.some(item => item?.canGenerateInvoice);
    const eligibleCount = (hasCanGenerateFlag ? list.filter(item => item?.canGenerateInvoice) : list).filter(
      item => item.shipments?.length,
    ).length;
    return eligibleCount > 0 && selectedAccountRows.length === eligibleCount;
  }, [list, selectedAccountRows.length]);

  const isSomeAccountsSelected = selectedAccountRows.length > 0 && !isAllAccountsSelected;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Section Header — Compact with minimal vertical footprint */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        sx={{ mb: 1, flexShrink: 0 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '7px',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Inventory2OutlinedIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: 'text.primary', lineHeight: 1.2 }}>
              Unbilled Accounts & Shipments
            </Typography>
            <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>
              Select accounts or expand rows to review individual shipments
            </Typography>
          </Box>
        </Stack>
      </Stack>

      {/* Compact Horizontally Scrollable Filter Toolbar — Single row, no multi-line wrapping */}
      <Box
        sx={{
          p: '6px 10px',
          mb: 1,
          borderRadius: '10px',
          bgcolor: alpha(theme.palette.primary.main, 0.025),
          border: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexWrap: 'nowrap',
          alignItems: 'center',
          gap: 1.25,
          flexShrink: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
          whiteSpace: 'nowrap',
          scrollbarWidth: 'thin',
          scrollbarColor: `${alpha(theme.palette.text.primary, 0.18)} transparent`,
          '&::-webkit-scrollbar': {
            height: '1px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(theme.palette.text.primary, 0.18),
            borderRadius: '4px',
            transition: 'background-color 0.2s ease',
          },
          '&:hover::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(theme.palette.text.primary, 0.28),
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: alpha(theme.palette.text.primary, 0.45),
          },
        }}
      >
        <Box sx={{ width: '270px', minWidth: '240px', flexShrink: 0 }}>
          <FilterSearch
            onSelectChange={onSearchSelectChange}
            list={SearchFilterList}
            selectValue={filterSelectValue}
            textValue={filterText}
            onTextChange={onSearchTextChange}
            onTextSearch={onTextSearch}
            onTextClear={onTextClear}
          />
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          <DateRangePicker onChange={onDateChange} value={dateRange} />
        </Box>
        <Box sx={{ width: '145px', minWidth: '130px', flexShrink: 0 }}>
          <FilterSelect
            id="modal-system"
            label="System"
            value={system}
            options={activeSystemList}
            onChange={onSystemFilter}
          />
        </Box>
        {orgName !== 'Cargo Solutions Network' && (
          <Box sx={{ width: '155px', minWidth: '140px', flexShrink: 0 }}>
            <FilterSelect
              id="modal-shipmenttype"
              label="Shipment Type"
              value={shipmentType}
              options={ShipmentTypeList}
              onChange={onTypeFilter}
            />
          </Box>
        )}
        <SbButton
          className="xs-text"
          variant="outlined"
          startIcon={<FilterAltOffOutlinedIcon fontSize="small" />}
          sx={{
            borderRadius: '8px !important',
            padding: '5px 12px !important',
            height: '36px !important',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
          onClick={onClearFilters}
        >
          Reset
        </SbButton>
      </Box>

      {/* Master Selection Sub-header Bar — Compact padding */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: { xs: 1.25, sm: 2 },
          py: 0.5,
          mb: 1,
          borderRadius: '8px',
          bgcolor: alpha(theme.palette.text.primary, 0.03),
          border: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Checkbox
            size="small"
            checked={isAllAccountsSelected}
            indeterminate={isSomeAccountsSelected}
            onChange={onSelectAllAccounts}
            sx={{ p: 0.4 }}
            inputProps={{ 'aria-label': 'Select all unbilled accounts on current page' }}
          />
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary' }}>
            Select All on Page
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary', fontWeight: 500 }}>
          Showing {list.length} of {totalCount || list.length} unbilled account(s)
        </Typography>
      </Box>

      {/* Scrollable Account & Shipment List — Expands to claim all recovered vertical space */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          pr: 0.75,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(theme.palette.text.primary, 0.15),
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: alpha(theme.palette.text.primary, 0.3),
          },
        }}
      >
        {list.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              minHeight: '220px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px',
              border: `1px dashed ${theme.palette.divider}`,
              p: 4,
              textAlign: 'center',
            }}
          >
            <Inventory2OutlinedIcon sx={{ fontSize: 38, color: 'text.disabled', mb: 1 }} />
            <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: 'text.primary' }}>
              {listLoading ? 'Loading unbilled shipments...' : 'No unbilled shipments found'}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mt: 0.5 }}>
              {listLoading
                ? 'Please wait while we fetch the latest unbilled account data.'
                : 'Try adjusting the date range, system, or search filters above.'}
            </Typography>
          </Box>
        ) : (
          list.map(data => {
            const groupKey = getAccountGroupKey(data);
            const selectedEntry = selectedAccountRows.find(item => isSameAccountEntry(item, data));
            const selectedShipments = selectedEntry?.shipments || EMPTY_SHIPMENTS;
            const isAccountExpanded = Boolean(expandedAccounts[groupKey]);

            return (
              <AccountShipmentCard
                key={groupKey}
                data={data}
                groupKey={groupKey}
                orgName={orgName}
                locale={locale}
                selectedShipments={selectedShipments}
                isExpanded={isAccountExpanded}
                onToggleExpandAccount={onToggleExpandAccount}
                onSelectAccountGroup={onSelectAccountGroup}
                onToggleSingleShipment={onToggleSingleShipment}
              />
            );
          })
        )}
      </Box>

      {/* Sticky Bottom Pagination — Compact height */}
      {totalCount > 0 && (
        <Box
          sx={{
            mt: 0.5,
            pt: 0.25,
            borderTop: `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
          }}
        >
          <TablePagination
            component="div"
            count={totalCount}
            page={currentPage - 1}
            rowsPerPage={perPage}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            sx={{
              '& .MuiTablePagination-toolbar': {
                minHeight: '36px !important',
                pl: 1,
                pr: 0,
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
});

export default ShipmentDetailsSection;
